import { useMemo } from 'react'
import { useAuth } from '@/lib/auth'
import { useDecisions } from '@/features/decisions/useDecisions'
import { useTasks } from '@/features/roadmap/useRoadmap'
import { useFieldDecisions } from '@/features/fields/useFields'
import { useProducts } from '@/features/products/useProducts'
import { useOwners } from '@/features/owners/useOwners'
import { useStaff } from '@/features/orgchart/useStaff'
import {
  commentRecords, decisionRecords, fieldRecords, ownerRecords, productRecords,
  staffRecords, taskRecords,
} from './records'
import { useSearchComments } from './useSearchComments'
import { ITEM_KINDS, KIND_CAPABILITY, type ItemKind, type SearchRecord } from './types'

/**
 * Everything this person is allowed to see, flattened into one list.
 *
 * Nothing is subscribed until the palette is opened for the first time — `active` stays
 * false until then, so somebody who never searches pays nothing for it. Once opened the
 * subscriptions stay, because the second search is the one where waiting is annoying, and
 * the tabs hold the same listeners anyway.
 *
 * Each collection is gated on the capability the rules enforce for it. Subscribing to a
 * collection a role cannot read does not return an empty list — it returns a permission
 * error, which would surface as a failure for a panel nobody asked for.
 */
export function useSearchRecords(active: boolean, open: boolean): {
  records: SearchRecord[]
  loading: boolean
  commentsUnavailable: boolean
} {
  const { can } = useAuth()

  const { decisions, loading: decisionsLoading } = useDecisions(active && can('decisions.view'))
  const { tasks, loading: tasksLoading } = useTasks(active && can('roadmap.view'))
  const { fields, loading: fieldsLoading } = useFieldDecisions(active && can('fields.view'))
  const { products, loading: productsLoading } = useProducts(active && can('products.view'))
  const { owners, loading: ownersLoading } = useOwners(active && can('owners.view'))
  // `staff` is not in `ITEM_KINDS` (see `types.ts`) — that list is what gates *comment*
  // subscriptions by kind, and indexing people here does not depend on it: a person's own
  // record comes from this collection directly, the same way an owner's does. Comments
  // written on a person stay out of the index for now, same as before this task — the kind
  // was deliberately left out of `ITEM_KINDS` because that is a separate piece of work.
  const { staff, loading: staffLoading } = useStaff(active && can('staff.view'))

  const items = useMemo(
    () => [
      ...decisionRecords(decisions),
      ...taskRecords(tasks),
      ...ownerRecords(owners),
      ...staffRecords(staff),
      ...fieldRecords(fields),
      ...productRecords(products),
    ],
    [decisions, tasks, owners, staff, fields, products],
  )

  // The discussion, gated the same way the items are — `firestore.rules` maps each kind
  // of comment to the capability for the tab it was written on.
  const readableKinds = ITEM_KINDS.filter((k: ItemKind) => can(KIND_CAPABILITY[k]))
  const { comments, loading: commentsLoading, failed } = useSearchComments(open, readableKinds)

  const records = useMemo(() => {
    // Comments say which item they hang under, so the items are indexed first. A comment
    // knows its parent's *document* id, and every kind here is addressed by that same id
    // — a decision's document id is its printed id, which is what `createDecision` and
    // the importer both write.
    const parents = new Map(items.map((r) => [`${r.kind}:${r.targetId}`, r]))
    return [...items, ...commentRecords(comments, parents)]
  }, [items, comments])

  return {
    records,
    loading:
      active &&
      (decisionsLoading || tasksLoading || fieldsLoading || productsLoading || ownersLoading
       || staffLoading || commentsLoading),
    commentsUnavailable: failed,
  }
}
